import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const idempotencyKeySchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)

const invoiceLineSchema = z
  .object({
    purchaseOrderItem: z.string().trim().min(1).max(140),
    itemMasterId: entityIdSchema,
    quantityMilli: z.number().int().positive(),
    unitCostMinor: z.number().int().nonnegative(),
  })
  .strict()

const requestBase = z
  .object({
    requestVersion: z.literal(1),
    correlationId: correlationIdSchema,
    idempotencyKey: idempotencyKeySchema,
    invoiceId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
  })
  .strict()

export const supplierInvoiceSubmitV1Schema = requestBase
  .extend({
    purchaseOrderId: entityIdSchema,
    expectedPurchaseOrderVersion: z.number().int().positive(),
    supplierInvoiceNumber: z.string().trim().min(1).max(140),
    invoiceDate: z.iso.date(),
    postingDate: z.iso.date(),
    dueDate: z.iso.date(),
    matchMode: z.enum(["two_way", "three_way"]),
    purchaseReceiptIds: z.array(entityIdSchema).max(20),
    quantityToleranceMilli: z.number().int().nonnegative(),
    amountToleranceMinor: z.number().int().nonnegative(),
    lines: z.array(invoiceLineSchema).min(1).max(100),
  })
  .superRefine((value, context) => {
    if (
      (value.matchMode === "three_way" &&
        value.purchaseReceiptIds.length === 0) ||
      (value.matchMode === "two_way" &&
        value.purchaseReceiptIds.length !== 0)
    )
      context.addIssue({
        code: "custom",
        path: ["purchaseReceiptIds"],
        message: "Receipt evidence must match the selected match mode.",
      })
    if (value.dueDate < value.invoiceDate)
      context.addIssue({
        code: "custom",
        path: ["dueDate"],
        message: "Due date cannot precede invoice date.",
      })
  })

export const supplierInvoiceResolveV1Schema = requestBase.extend({
  decision: z.enum(["approve", "reject"]),
  reason: z.string().trim().min(1).max(1_000),
})

export const supplierInvoiceCreditV1Schema = requestBase.extend({
  creditId: entityIdSchema,
  postingDate: z.iso.date(),
  amountMinor: z.number().int().positive(),
  reason: z.string().trim().min(1).max(1_000),
})

export const supplierInvoiceResultV1Schema = z
  .object({
    entityType: z.literal("supplier_invoice_match"),
    entityId: entityIdSchema,
    state: z.enum([
      "exception",
      "ready_for_payment",
      "rejected",
      "partially_credited",
      "credited",
    ]),
    version: z.number().int().positive(),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    purchaseOrderId: entityIdSchema,
    matchMode: z.enum(["two_way", "three_way"]),
    quantityVarianceMilli: z.number().int().nonnegative(),
    amountVarianceMinor: z.number().int().nonnegative(),
    exceptionCodes: z.array(
      z.enum(["quantity_tolerance", "amount_tolerance"]),
    ),
    purchaseInvoice: z.string().trim().min(1).nullable(),
    payableId: entityIdSchema.nullable(),
    creditNote: z.string().trim().min(1).nullable(),
    paymentReady: z.boolean(),
  })
  .strict()

export type SupplierInvoiceSubmitV1 = z.infer<
  typeof supplierInvoiceSubmitV1Schema
>
